import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import InjectorDetailCard from "@/components/InjectorDetailCard/InjectorDetailCard";
import Nav from "@/components/Header/Nav/Nav";
import Footer from "@/components/Footer/Footer";
import styles from "./InjectorDetailPage.module.scss";
import InjectorCard from "@/components/InjectorCard/InjectorCard";
import { useCart } from "@/hooks/useCart";
import CardSlider from "@/components/Slider/Slider";
import { Helmet } from "react-helmet";
import useFormattedName from "@/hooks/useFormattedName";

interface Injector {
  goid: string;
  catid: string;
  name: string;
  descr: string;
  pricevat: number;
  priceexchvat: number;
  currency: string;
  files: { image?: { [key: number]: string } };
}

const InjectorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [injector, setInjector] = useState<Injector | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarProducts, setSimilarProducts] = useState<Injector[]>([]);
  const { addToCart } = useCart();
  const formattedName = useFormattedName(injector?.name || "");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchInjector = async () => {
      if (!id) {
        setError("Invalid injector ID.");
        setLoading(false);
        return;
      }

      try {
        const searchResponse = await fetch(
          `https://crm.hixa.eu/external/goodsapi/?type=good&goid=${id}&legid=1`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer DH345DFGDGb32FDsf03876SDFSdf13`,
              Accept: "application/json",
            },
          }
        );

        const searchData = await searchResponse.json();
        if (!searchData || searchData.data.length === 0) {
          setError("Injector not found");
        } else {
          const injector = searchData.data[0];
          setInjector(injector);
          fetchSimilarProducts(injector.name, injector.goid);
        }
      } catch (error) {
        setError("Error fetching injector details");
      } finally {
        setLoading(false);
      }
    };

    const fetchSimilarProducts = async (name: string, currentGoid: string) => {
      try {
        const response = await fetch("https://crm.hixa.eu/external/goodsapi/", {
          method: "POST",
          headers: {
            Authorization: `Bearer DH345DFGDGb32FDsf03876SDFSdf13`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: "good",
            onlyprice: "1",
            start: "0",
            limit: "10",
            search: name,
            legid: "1",
          }),
        });

        const data = await response.json();
        const filtered = data?.data?.filter(
          (item: Injector) => item.goid !== currentGoid
        );

        setSimilarProducts(filtered?.slice(0, 5) || []);
      } catch (err) {
        console.error("Error loading similar products:", err);
      }
    };

    fetchInjector();
  }, [id]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;
  if (!injector) return <p>Injector data is missing.</p>;

  // Обрізаємо title до 70 символів
  const title =
    injector.name.length > 70
      ? `${injector.name.slice(0, 67)}...`
      : injector.name;

  // Обрізаємо description до 150 символів
  const description =
    injector.descr.length > 150
      ? `${injector.descr.slice(0, 147)}...`
      : injector.descr;

  // Обробляємо масив зображень
  const imageUrls = injector.files?.image
    ? Object.values(injector.files.image).slice(0, 4)
    : ["/images/Shop/template.webp"];

  // Налаштування слайдера
  const sliderSettings = {
    slidesPerView: 5,
    spaceBetween: 12,
    loop: true,
    breakpoints: {
      1025: {
        slidesPerView: 5,
      },
      478: {
        slidesPerView: 3,
      },
      0: {
        slidesPerView: 2,
      },
    },
  };

  const schemaOrgData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: injector.name,
    description: injector.descr,
    sku: injector.goid,
    offers: {
      "@type": "Offer",
      url: `https://hixa.eu/product/${injector.goid}/${formattedName}`,
      priceCurrency: injector.currency,
      pricevat: injector.pricevat,
      // exchangePrice: injector.exchangeprice,

      seller: {
        "@type": "Organization",
        name: "Hixa",
      },
    },
    images: injector.files?.image
      ? Object.values(injector.files.image)
      : ["/images/Shop/template.webp"],
  };

  return (
    <div className="pageSection">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgData)}
        </script>
      </Helmet>
      <Nav />
      <InjectorDetailCard
        id={injector.goid}
        catid={injector.catid}
        name={injector.name}
        pricevat={injector.pricevat}
        priceexchvat={injector.priceexchvat}
        descr={injector.descr}
        images={imageUrls}
        onAddToCart={(goid, quantity, typeorder) =>
          addToCart(
            {
              goid,
              typeorder,
            },
            quantity
          )
        }
      />

      {/* Слайдер для схожих товарів */}
      {similarProducts.length > 0 && (
        <div className="container">
          <section className={`${styles.similarSection} sideP64`}>
            <h3 className={`brxeHeading sideP24 ${styles.similarTitle}`}>
              Ähnliche Produkte
            </h3>
            <CardSlider settings={sliderSettings}>
              {similarProducts.map((product) => {
                const productImage = product.files?.image
                  ? Object.values(product.files.image)[0]
                  : "/images/Shop/template.webp";

                return (
                  <InjectorCard
                    key={product.goid}
                    goid={product.goid}
                    name={product.name}
                    price={product.pricevat}
                    imageUrl={productImage}
                  />
                );
              })}
            </CardSlider>
          </section>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default InjectorDetailPage;
